import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../../services/firebase';
import { generateUniqueSlug } from '../../utils/slugUtils';
import './BlogForm.css';

const BlogForm = ({ blog, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: blog?.title || '',
    excerpt: blog?.excerpt || '',
    content: blog?.content || '',
    category: blog?.category || 'gardening-tips',
    author: blog?.author || 'DHRTI MAA Team',
    publishedAt: blog?.publishedAt || new Date().toISOString().split('T')[0],
    readTime: blog?.readTime || '5 min read',
    tags: blog?.tags || [],
    views: blog?.views || 0,
    likes: blog?.likes || 0,
    isPublished: blog?.isPublished ?? true,
    isFeatured: blog?.isFeatured ?? false,
    image: blog?.image || '',
    images: blog?.images || []
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [tagText, setTagText] = useState('');

  const categories = [
    { id: 'gardening-tips', name: 'Gardening Tips' },
    { id: 'fertilizers', name: 'Fertilizers' },
    { id: 'seasonal', name: 'Seasonal Guide' },
    { id: 'composting', name: 'Composting' },
    { id: 'plant-care', name: 'Plant Care' },
    { id: 'container-gardening', name: 'Container Gardening' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (files) => {
    if (files.length === 0) return;
    
    setUploadingImages(true);
    const uploadedUrls = [];
    
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('You must be logged in to upload images. Please log in and try again.');
      }
      
      console.log('Starting image upload...', { fileCount: files.length, user: auth.currentUser.email });
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not an image. Please select image files only.`);
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        }
        
        const imageRef = ref(storage, `blogs/${Date.now()}_${file.name}`);
        console.log('Uploading file:', file.name, 'to:', imageRef.fullPath);
        
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        console.log('Upload successful, URL:', url);
        uploadedUrls.push(url);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
        image: uploadedUrls[0] // Set first image as featured image
      }));
      
      console.log('All images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Show detailed error message
      let errorMessage = 'Error uploading images. ';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage += 'Permission denied. Please check Firebase Storage security rules and ensure you are logged in.';
      } else if (error.code === 'storage/canceled') {
        errorMessage += 'Upload was canceled.';
      } else if (error.code === 'storage/unknown') {
        errorMessage += 'Unknown error occurred. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check the browser console for more details.';
      }
      
      alert(errorMessage);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      image: prev.images.length === 1 ? '' : prev.image
    }));
  };

  const addTag = () => {
    if (tagText.trim() && !formData.tags.includes(tagText.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagText.trim()]
      }));
      setTagText('');
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Generate unique slug for the blog
      const slug = await generateUniqueSlug(formData.title, 'blogs', blog?.id);
      
      // Add slug to form data
      const dataWithSlug = {
        ...formData,
        slug: slug
      };
      
      await onSubmit(dataWithSlug);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-form-container">
      <div className="form-header">
        <h2>{blog ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="blog-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="title">Blog Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="Enter blog title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Excerpt *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              required
              rows="3"
              placeholder="Brief description of the blog post"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows="15"
              placeholder="Write your blog content here..."
            />
          </div>
        </div>

        {/* Blog Details */}
        <div className="form-section">
          <h3>Blog Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="author">Author *</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                required
                placeholder="Author name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="publishedAt">Publish Date *</label>
              <input
                type="date"
                id="publishedAt"
                name="publishedAt"
                value={formData.publishedAt}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="readTime">Read Time</label>
              <input
                type="text"
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleInputChange}
                placeholder="5 min read"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h3>Blog Images</h3>
          
          <div className="image-upload">
            <input
              type="file"
              id="images"
              multiple
              accept="image/*"
              onChange={(e) => handleImageUpload(Array.from(e.target.files))}
              className="file-input"
            />
            <label htmlFor="images" className="file-label">
              {uploadingImages ? 'Uploading...' : 'Choose Images'}
            </label>
            <p className="upload-hint">You can select multiple images. First image will be used as featured image.</p>
          </div>

          {formData.images.length > 0 && (
            <div className="image-preview">
              {formData.images.map((url, index) => (
                <div key={index} className="preview-item">
                  <img src={url} alt={`Blog ${index + 1}`} />
                  {index === 0 && <div className="featured-badge">Featured</div>}
                  <button
                    type="button"
                    className="remove-image"
                    onClick={() => removeImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="form-section">
          <h3>Tags</h3>
          
          <div className="tag-input">
            <input
              type="text"
              placeholder="Enter tag"
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            />
            <button type="button" onClick={addTag}>
              Add Tag
            </button>
          </div>

          <div className="tags-list">
            {formData.tags.map((tag, index) => (
              <div key={index} className="tag-item">
                <span>#{tag}</span>
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="remove-tag"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics */}
        <div className="form-section">
          <h3>Statistics</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="views">Views</label>
              <input
                type="number"
                id="views"
                name="views"
                value={formData.views}
                onChange={handleInputChange}
                min="0"
                placeholder="0"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="likes">Likes</label>
              <input
                type="number"
                id="likes"
                name="likes"
                value={formData.likes}
                onChange={handleInputChange}
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleInputChange}
              />
              Publish this blog post
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
              />
              Featured Blog (appears in home page "Latest from Our Blog" section)
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || uploadingImages}
          >
            {loading ? 'Saving...' : (blog ? 'Update Blog Post' : 'Add Blog Post')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
